import React, { useEffect, useMemo, useState } from 'react';
import { useLogout } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
} from '@mui/material';
import { useUser } from '@/context/UserContext';
import './splash-screen.css';

export const SplashScreen: React.FC = () => {
  const { mutate: logout } = useLogout();
  const navigate = useNavigate();
  const { setProduct: setUserProduct, setRegion: setUserRegion, setRole: setUserRole } = useUser();

  const products = useMemo(() => ['Chemicals', 'Lubes'], []);
  const regions = useMemo(() => ['AMP', 'EUP', 'APP'], []);
  const roles = useMemo(
    () => [
      'Customer Service',
      'Replenisher / Raw Materials Coordinator',
      'Supply Point / Site Logistics',
      'Supply Planner',
      'Transportation Scheduler',
      'Production Planning / Quality',
      'View Only',
    ],
    [],
  );

  const [product, setProduct] = useState('');
  const [region, setRegion] = useState('');
  const [role, setRole] = useState('');

  useEffect(() => {
    setUserProduct('');
    setUserRegion('');
    setUserRole('');
  }, [setUserProduct, setUserRegion, setUserRole]);

  const leftRoles = roles.slice(0, Math.trunc(roles.length / 2));
  const rightRoles = roles.slice(Math.trunc(roles.length / 2));

  const getStartedButtonDisabled = () => {
    if (!product) return true;
    if (product === 'Lubes' && (!role || !region)) return true;
    if (product === 'Chemicals' && !role) return true;
    return false;
  };

  const onBusinessLineChange = (value: string) => {
    setProduct(value);
    if (value === 'Chemicals') setRegion('G9P');
    else setRegion('');
  };

  const getNavigationUrl = (selectedRole: string) => {
    switch (selectedRole) {
      case 'Supply Point / Site Logistics':
        return '/supply-point?type=loading';
      case 'Transportation Scheduler':
        return '/scheduling';
      case 'Replenisher / Raw Materials Coordinator':
        return '/orders';
      case 'Production Planning / Quality':
        return '/orders';
      case 'Supply Planner':
        return '/orders';
      case 'Customer Service':
      case 'Viewer':
      case 'View Only':
        return '/orders';
      default:
        return '/orders';
    }
  };

  const onGetStarted = () => {
    setUserProduct(product);
    setUserRegion(region);
    setUserRole(role);
    navigate(getNavigationUrl(role));
  };

  return (
    <Box className="splash-container">
      <Card variant="outlined" className="splash-card">
        <Box component="header" className="splash-header">
          <h2 className="splash-title">
            Welcome to ExxonMobil Cyber BCP{' '}
            Supply Chain Order&#8209;to&#8209;Delivery
          </h2>
        </Box>

        <p className="splash-subtitle">
          Please select a business line and role to get started. This application
          was designed for Chemicals and Lubes Supply Chain BCP process. Only trained
          staff are authorized to access it. Do not proceed if you have not been
          trained or have received specific direction from your Management. Access is
          logged.
        </p>

        <Box className="splash-content">
          <Box className="splash-grid">
            <Box className="radio-tile">
              <Box className="radio-group-list">
                <h3 className="radio-group-header">Select business line</h3>
                <FormControl>
                  <RadioGroup
                    id="qa-businessLine-rdo"
                    className="splash-radio-group"
                    value={product}
                    onChange={(e) => onBusinessLineChange(e.target.value)}
                  >
                    {products.map((item) => (
                      <FormControlLabel
                        key={item}
                        id={`qa-${item}-rdo`}
                        className="splash-radio-button"
                        value={item}
                        control={<Radio sx={{ color: 'rgba(0,0,0,0.6)', '&.Mui-checked': { color: '#0c479d' } }} />}
                        label={<span className="word-wrap">{item}</span>}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>

            <Box className="radio-tile">
              <Box className="radio-group-list">
                <h3 className={`radio-group-header${product !== 'Lubes' ? ' disabled-header' : ''}`}>
                  Select a region for lubes
                </h3>
                <FormControl>
                  <RadioGroup
                    id="qa-region-rdo"
                    className="splash-radio-group"
                    value={region}
                    onChange={(e) => setRegion(e.target.value)}
                  >
                    {regions.map((item) => (
                      <FormControlLabel
                        key={item}
                        id={`qa-${item}-rdo`}
                        className="splash-radio-button"
                        value={item}
                        disabled={product !== 'Lubes'}
                        control={<Radio sx={{ color: 'rgba(0,0,0,0.6)', '&.Mui-checked': { color: '#0c479d' } }} />}
                        label={item}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>

            <Box className="radio-tile">
              <Box className="radio-group-list">
                <h3 className={`radio-group-header${!product ? ' disabled-header' : ''}`}>
                  Select a role
                </h3>
                <FormControl>
                  <RadioGroup
                    id="qa-roleCol1-rdo"
                    className="splash-radio-group"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  >
                    {leftRoles.map((item) => (
                      <FormControlLabel
                        key={item}
                        id={`qa-${item}-rdo`}
                        className="splash-radio-button"
                        value={item}
                        disabled={!product}
                        control={<Radio sx={{ color: 'rgba(0,0,0,0.6)', '&.Mui-checked': { color: '#0c479d' } }} />}
                        label={<span className="word-wrap">{item}</span>}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>

            <Box className="radio-tile bottom-right-tile">
              <Box className="radio-group-list">
                <h3 className="radio-group-header dummy-header">Dummy Header</h3>
                <FormControl>
                  <RadioGroup
                    id="qa-roleCol2-rdo"
                    className="splash-radio-group"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  >
                    {rightRoles.map((item) => (
                      <FormControlLabel
                        key={item}
                        id={`qa-${item}-rdo`}
                        className="splash-radio-button"
                        value={item}
                        disabled={!product}
                        control={<Radio sx={{ color: 'rgba(0,0,0,0.6)', '&.Mui-checked': { color: '#0c479d' } }} />}
                        label={<span className="word-wrap">{item}</span>}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>
            </Box>

          </Box>
        </Box>

        <Box className="splash-footer">
          <a
            id="qa-signOut-btn"
            className="splash-signout button-text"
            href="/.auth/logout"
            onClick={(e) => { e.preventDefault(); logout(); }}
          >
            SIGN OUT
          </a>
          <Button
            id="qa-getStarted-btn"
            variant="contained"
            disabled={getStartedButtonDisabled()}
            onClick={onGetStarted}
            sx={{
              bgcolor: '#0c479d',
              color: '#fff',
              letterSpacing: '.08em',
              maxHeight: 36,
              '&:hover': { bgcolor: '#0a3d8f' },
              '&.Mui-disabled': { bgcolor: '#e0e0e0', color: '#9e9e9e' },
            }}
          >
            GET STARTED
          </Button>
        </Box>

      </Card>
    </Box>
  );
};
